import React, { useEffect, useMemo, useState } from "react";
import { FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import { generateUserId, normalizeIframeMarkup, slugify } from "../../utils/users";
import classes from "./AddEditUser.module.css";

const emptyUser = {
  id: "",
  fullName: "",
  passwordCode: "",
  slug: "",
  iframeCode: "",
  status: "active",
};

const generatePasscode = () => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$%";

  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const bytes = new Uint32Array(12);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
  }

  return `PC-${Math.random().toString(36).slice(2, 10)}`;
};

export default function AddEditUser({
  open,
  user,
  onClose,
  onSubmit,
  loading = false,
  users = [],
}) {
  const [form, setForm] = useState(emptyUser);
  const [showPassword, setShowPassword] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!open) return;

    const next = user || {};
    setForm({
      id: String(next.id || generateUserId(users)).trim(),
      fullName: String(next.fullName || next.name || "").trim(),
      passwordCode: String(next.passwordCode || "").trim(),
      slug: String(next.slug || "").trim(),
      iframeCode: String(next.iframeCode || "").trim(),
      status: next.status === "inactive" ? "inactive" : "active",
    });
    setSlugTouched(false);
    setShowPassword(false);
  }, [open, user, users]);

  useEffect(() => {
    if (open) return;

    setForm(emptyUser);
    setSlugTouched(false);
    setShowPassword(false);
  }, [open]);

  const title = useMemo(
    () => (user?.isNew ? "Add User" : "Edit User"),
    [user],
  );

  if (!open) return null;

  const safeForm = form && typeof form === "object" ? form : emptyUser;

  const update = (name) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "fullName" && !slugTouched) {
        next.slug = String(value || "").trim() ? slugify(value) : "";
      }

      if (name === "slug") {
        next.slug = value;
        setSlugTouched(true);
      }

      return next;
    });
  };

  const normalizeIframeField = () => {
    setForm((prev) => ({
      ...prev,
      iframeCode: normalizeIframeMarkup(prev.iframeCode),
    }));
  };

  return (
    <div className={classes.overlay} onMouseDown={onClose}>
      <div className={classes.modal} onMouseDown={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        <div className={classes.badgeRow}>
          <span className={classes.badge}>Auto ID: {safeForm.id || "Generating..."}</span>
        </div>
        <div className={classes.stack}>
          <label className={classes.field}>
            <span>Full Name *</span>
            <input
              className="inputBase"
              name="user_full_name"
              autoComplete="off"
              value={safeForm.fullName}
              onChange={update("fullName")}
              placeholder="Ali Khan"
              required
            />
          </label>

          <div className={classes.rowOne}>
            <label className={classes.field}>
              <span>Passcode *</span>
              <div className={classes.inputWrap}>
                <FiLock />
                <input
                  className="inputBase"
                  type={showPassword ? "text" : "password"}
                  name="user_password_code"
                  autoComplete="new-password"
                  value={safeForm.passwordCode}
                  onChange={update("passwordCode")}
                  placeholder="Enter passcode"
                  required
                />
                <button
                  type="button"
                  className={classes.eye}
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide passcode" : "Show passcode"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <button
                type="button"
                className={classes.generate}
                onClick={() => setForm((prev) => ({ ...prev, passwordCode: generatePasscode() }))}
              >
                Generate Passcode
              </button>
            </label>
          </div>

          <div className={classes.rowOne}>
            <label className={classes.field}>
              <span>Slug</span>
              <input
                className="inputBase"
                name="user_slug"
                autoComplete="off"
                value={safeForm.slug}
                onChange={update("slug")}
                placeholder="ali-khan"
              />
            </label>
          </div>

          <div className={classes.rowOne}>
            <label className={classes.field}>
              <span>Iframe *</span>
              <textarea
                className="textareaBase"
                rows={6}
                name="user_iframe_code"
                autoComplete="off"
                value={safeForm.iframeCode}
                onChange={update("iframeCode")}
                onBlur={normalizeIframeField}
                placeholder={"<iframe src=\"https://...\" allowfullscreen></iframe>"}
                required
              />
              <small className="muted">Paste iframe code or a URL. It will be saved as iframe markup.</small>
            </label>
          </div>

          <div className={classes.rowOne}>
            <label className={classes.field}>
              <span>Status</span>
              <select className="selectBase" value={safeForm.status} onChange={update("status")}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
          </div>
        </div>
        <div className={classes.actions}>
          <button type="button" className="btnBase btnSecondary" onClick={onClose}>
            Close
          </button>
          <button
          type="button"
          className="btnBase btnPrimary"
          disabled={loading}
          onClick={() => onSubmit?.(safeForm, setForm)}
        >
            {loading ? "Saving..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
